import React, { useEffect, useMemo, useState, useRef } from "react";
import { adminApi } from "../../api/client";
import { DocumentList } from "../../Components/DocumentViewer";

export default function Home() {
  const [adminListings, setAdminListings] = useState([]);
  const [channelPartners, setChannelPartners] = useState([]);
  const [promoters, setPromoters] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedPromoter, setSelectedPromoter] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [providerPaymentVerification, setProviderPaymentVerification] =
    useState([]);
  const [statistics, setStatistics] = useState({
    totalChannelPartners: 0,
    totalPromoters: 0,
    totalProviders: 0,
    totalListings: 0,
    pendingChannelPartners: 0,
    pendingPromoters: 0,
    pendingListings: 0,
    approvedChannelPartners: 0,
    approvedPromoters: 0,
    approvedListings: 0,
    // Provider payment verification stats
    totalProviderPayments: 0,
    pendingProviderPayments: 0,
    verifiedProviderPayments: 0,
    rejectedProviderPayments: 0,
  });

  // Refs for scrolling to specific sections
  const channelPartnersRef = useRef(null);
  const promotersRef = useRef(null);
  const listingsRef = useRef(null);
  const providerPaymentsRef = useRef(null);

  // Scroll handler function
  const scrollToSection = (ref) => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    (async () => {
      try {
        // Fetch dashboard data and comprehensive statistics
        const [
          listingsRes,
          channelPartnersRes,
          promotersRes,
          providersRes,
          allChannelPartnersRes,
          allPromotersRes,
          allListingsRes,
          approvedChannelPartnersRes,
          approvedPromotersRes,
          approvedListingsRes,
        ] = await Promise.all([
          // Pending items for approval
          adminApi.listListings({
            status: "pending_approval",
            page: 1,
            limit: 20,
          }),
          adminApi.listChannelPartners({
            status: "pending",
            page: 1,
            limit: 20,
          }),
          adminApi.listPromoters({
            status: "pending_approval",
            page: 1,
            limit: 20,
          }),
          adminApi.listProviders({
            page: 1,
            limit: 1000,
          }),
          // All channel partners
          adminApi.listChannelPartners({
            page: 1,
            limit: 1000,
          }),
          // All promoters
          adminApi.listPromoters({
            page: 1,
            limit: 1000,
          }),
          // All listings
          adminApi.listListings({
            page: 1,
            limit: 1000,
          }),
          // Approved channel partners
          adminApi.listChannelPartners({
            status: "approved",
            page: 1,
            limit: 1000,
          }),
          // Approved promoters
          adminApi.listPromoters({
            status: "approved",
            page: 1,
            limit: 1000,
          }),
          // Approved listings
          adminApi.listListings({
            status: "approved",
            page: 1,
            limit: 1000,
          }),
        ]);

        // Set pending items for approval
        setAdminListings(listingsRes?.items || []);
        setChannelPartners(channelPartnersRes?.items || []);
        setPromoters(promotersRes?.items || []);
        setProviders(providersRes?.items || []);

        // Filter providers with pending payments
        const pendingPaymentProviders = (providersRes?.items || []).filter(
          (provider) => provider?.paymentStatus === "pending"
        );

        setProviderPaymentVerification(pendingPaymentProviders);

        // Calculate provider payment verification statistics
        const allProviders = providersRes?.items || [];
        const totalProviderPayments = allProviders.length;
        const pendingProviderPayments = allProviders.filter(
          (provider) => provider?.paymentStatus === "pending"
        ).length;
        const verifiedProviderPayments = allProviders.filter(
          (provider) => provider?.paymentStatus === "verified"
        ).length;
        const rejectedProviderPayments = allProviders.filter(
          (provider) => provider?.paymentStatus === "rejected"
        ).length;

        // Set comprehensive statistics
        setStatistics({
          totalChannelPartners: allChannelPartnersRes?.total || 0,
          totalPromoters: allPromotersRes?.total || 0,
          totalProviders:
            providersRes?.total || providersRes?.items?.length || 0,
          totalListings: allListingsRes?.total || 0,
          pendingChannelPartners:
            channelPartnersRes?.total || channelPartnersRes?.items?.length || 0,
          pendingPromoters:
            promotersRes?.total || promotersRes?.items?.length || 0,
          pendingListings:
            listingsRes?.total || listingsRes?.items?.length || 0,
          approvedChannelPartners:
            approvedChannelPartnersRes?.total ||
            approvedChannelPartnersRes?.items?.length ||
            0,
          approvedPromoters:
            approvedPromotersRes?.total ||
            approvedPromotersRes?.items?.length ||
            0,
          approvedListings:
            approvedListingsRes?.total ||
            approvedListingsRes?.items?.length ||
            0,
          // Provider payment verification stats
          totalProviderPayments,
          pendingProviderPayments,
          verifiedProviderPayments,
          rejectedProviderPayments,
        });
      } catch (_e) {
        console.error("Dashboard data fetch error:", _e);
        setAdminListings([]);
        setChannelPartners([]);
        setPromoters([]);
      }
    })();
  }, []);

  const providerIdToName = React.useMemo(() => {
    const idToName = {};
    (providers || []).forEach((p) => {
      const name = p?.providerProfile?.companyName || p?.email || p?.id;
      if (p?.id) idToName[p.id] = name;
    });
    return idToName;
  }, [providers]);

  const currencyFmt = useMemo(
    () =>
      new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    []
  );

  // Function to refresh statistics after actions
  const refreshStatistics = async () => {
    try {
      const [
        allChannelPartnersRes,
        allPromotersRes,
        allListingsRes,
        allProvidersRes,
        approvedChannelPartnersRes,
        approvedPromotersRes,
        approvedListingsRes,
      ] = await Promise.all([
        adminApi.listChannelPartners({ page: 1, limit: 1000 }),
        adminApi.listPromoters({ page: 1, limit: 1000 }),
        adminApi.listListings({ page: 1, limit: 1000 }),
        adminApi.listProviders({
          page: 1,
          limit: 1000,
        }),
        adminApi.listChannelPartners({
          status: "approved",
          page: 1,
          limit: 1000,
        }),
        adminApi.listPromoters({ status: "approved", page: 1, limit: 1000 }),
        adminApi.listListings({ status: "approved", page: 1, limit: 1000 }),
      ]);

      // Calculate provider payment verification statistics
      const allProviders = allProvidersRes?.items || [];
      const totalProviderPayments = allProviders.length;
      const pendingProviderPayments = allProviders.filter(
        (provider) => provider?.paymentStatus === "pending"
      ).length;
      const verifiedProviderPayments = allProviders.filter(
        (provider) => provider?.paymentStatus === "verified"
      ).length;
      const rejectedProviderPayments = allProviders.filter(
        (provider) => provider?.paymentStatus === "rejected"
      ).length;

      setStatistics((prev) => ({
        ...prev,
        totalChannelPartners: allChannelPartnersRes?.total || 0,
        totalPromoters: allPromotersRes?.total || 0,
        totalProviders:
          allProvidersRes?.total || allProvidersRes?.items?.length || 0,
        totalListings: allListingsRes?.total || 0,
        approvedChannelPartners:
          approvedChannelPartnersRes?.total ||
          approvedChannelPartnersRes?.items?.length ||
          0,
        approvedPromoters:
          approvedPromotersRes?.total ||
          approvedPromotersRes?.items?.length ||
          0,
        approvedListings:
          approvedListingsRes?.total || approvedListingsRes?.items?.length || 0,
        // Provider payment verification stats
        totalProviderPayments,
        pendingProviderPayments,
        verifiedProviderPayments,
        rejectedProviderPayments,
      }));
    } catch (error) {
      console.error("Error refreshing statistics:", error);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <h2 className="text-3xl font-bold mb-6 text-gray-800 text-center">
        Admin Dashboard
      </h2>

      {/* Welcome card with comprehensive stats */}
      <div className="bg-white shadow-xl rounded-xl p-8 border border-gray-200 w-full max-w-6xl mx-auto mb-5">
        {/* Main Statistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-center mb-6">
          <div
            className={`p-4 rounded-lg border transition-all ${
              statistics.pendingChannelPartners > 0
                ? "bg-red-100 border-red-400 shadow-md"
                : "bg-blue-50 border-blue-200"
            }`}
          >
            {statistics.pendingChannelPartners > 0 && (
              <div className="text-red-600 text-xl mb-1">⚠️</div>
            )}
            <div
              className={`font-bold ${
                statistics.pendingChannelPartners > 0
                  ? "text-4xl text-red-700"
                  : "text-3xl text-blue-600"
              }`}
            >
              {statistics.pendingChannelPartners}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Approved: {statistics.approvedChannelPartners} | Total:{" "}
              {statistics.totalChannelPartners}
            </div>
            <div className="text-sm text-gray-600 mb-1 mt-1">
              Channel Partners Pending
            </div>
          </div>
          <div
            className={`p-4 rounded-lg border transition-all ${
              statistics.pendingPromoters > 0
                ? "bg-red-100 border-red-400 shadow-md"
                : "bg-yellow-50 border-yellow-200"
            }`}
          >
            {statistics.pendingPromoters > 0 && (
              <div className="text-red-600 text-xl mb-1">⚠️</div>
            )}
            <div
              className={`font-bold ${
                statistics.pendingPromoters > 0
                  ? "text-4xl text-red-700"
                  : "text-3xl text-yellow-600"
              }`}
            >
              {statistics.pendingPromoters}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Approved: {statistics.approvedPromoters} | Total:{" "}
              {statistics.totalPromoters}
            </div>
            <div className="text-sm text-gray-600 mb-1 mt-1">
              Promoters Pending
            </div>
          </div>
          <div
            className={`p-4 rounded-lg border transition-all ${
              statistics.pendingProviderPayments > 0
                ? "bg-red-100 border-red-400 shadow-md"
                : "bg-green-50 border-green-200"
            }`}
          >
            {statistics.pendingProviderPayments > 0 && (
              <div className="text-red-600 text-xl mb-1">⚠️</div>
            )}
            <div
              className={`font-bold ${
                statistics.pendingProviderPayments > 0
                  ? "text-4xl text-red-700"
                  : "text-3xl text-green-600"
              }`}
            >
              {statistics.pendingProviderPayments}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Verified: {statistics.verifiedProviderPayments} | Total:{" "}
              {statistics.totalProviders}
            </div>
            <div className="text-sm text-gray-600 mb-1 mt-1">
              Provider Payments Pending
            </div>
          </div>
          <div
            className={`p-4 rounded-lg border transition-all ${
              statistics.pendingListings > 0
                ? "bg-red-100 border-red-400 shadow-md"
                : "bg-purple-50 border-purple-200"
            }`}
          >
            {statistics.pendingListings > 0 && (
              <div className="text-red-600 text-xl mb-1">⚠️</div>
            )}
            <div
              className={`font-bold ${
                statistics.pendingListings > 0
                  ? "text-4xl text-red-700"
                  : "text-3xl text-purple-600"
              }`}
            >
              {statistics.pendingListings}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Approved: {statistics.approvedListings} | Total:{" "}
              {statistics.totalListings}
            </div>
            <div className="text-sm text-gray-600 mb-1 mt-1">
              Listings Pending
            </div>
          </div>
        </div>

        {/* Pending Approvals Section */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">
            Pending Approvals
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-center">
            <div
              onClick={() =>
                channelPartners.length > 0 &&
                scrollToSection(channelPartnersRef)
              }
              className={`p-4 rounded-lg border transition-all ${
                statistics.pendingChannelPartners > 0
                  ? "bg-red-100 border-red-400 shadow-md"
                  : "bg-red-50 border-red-200"
              } ${
                channelPartners.length > 0
                  ? "cursor-pointer hover:shadow-lg hover:scale-105"
                  : "cursor-default"
              }`}
            >
              {statistics.pendingChannelPartners > 0 && (
                <div className="text-red-600 text-xl mb-1">⚠️</div>
              )}
              <div
                className={`font-bold ${
                  statistics.pendingChannelPartners > 0
                    ? "text-4xl text-red-700"
                    : "text-2xl text-red-600"
                }`}
              >
                {statistics.pendingChannelPartners}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Approved: {statistics.approvedChannelPartners} | Total:{" "}
                {statistics.totalChannelPartners}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                Channel Partners Pending
              </div>
            </div>
            <div
              onClick={() =>
                promoters.length > 0 && scrollToSection(promotersRef)
              }
              className={`p-4 rounded-lg border transition-all ${
                statistics.pendingPromoters > 0
                  ? "bg-orange-100 border-orange-400 shadow-md"
                  : "bg-orange-50 border-orange-200"
              } ${
                promoters.length > 0
                  ? "cursor-pointer hover:shadow-lg hover:scale-105"
                  : "cursor-default"
              }`}
            >
              {statistics.pendingPromoters > 0 && (
                <div className="text-orange-600 text-xl mb-1">⚠️</div>
              )}
              <div
                className={`font-bold ${
                  statistics.pendingPromoters > 0
                    ? "text-4xl text-orange-700"
                    : "text-2xl text-orange-600"
                }`}
              >
                {statistics.pendingPromoters}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Approved: {statistics.approvedPromoters} | Total:{" "}
                {statistics.totalPromoters}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                Promoters Pending
              </div>
            </div>
            <div
              onClick={() =>
                providerPaymentVerification.length > 0 &&
                scrollToSection(providerPaymentsRef)
              }
              className={`p-4 rounded-lg border transition-all ${
                statistics.pendingProviderPayments > 0
                  ? "bg-indigo-100 border-indigo-400 shadow-md"
                  : "bg-indigo-50 border-indigo-200"
              } ${
                providerPaymentVerification.length > 0
                  ? "cursor-pointer hover:shadow-lg hover:scale-105"
                  : "cursor-default"
              }`}
            >
              {statistics.pendingProviderPayments > 0 && (
                <div className="text-indigo-600 text-xl mb-1">⚠️</div>
              )}
              <div
                className={`font-bold ${
                  statistics.pendingProviderPayments > 0
                    ? "text-4xl text-indigo-700"
                    : "text-2xl text-indigo-600"
                }`}
              >
                {statistics.pendingProviderPayments}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Verified: {statistics.verifiedProviderPayments} | Total:{" "}
                {statistics.totalProviderPayments}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                Provider Payments Pending
              </div>
            </div>
            <div
              onClick={() =>
                adminListings.length > 0 && scrollToSection(listingsRef)
              }
              className={`p-4 rounded-lg border transition-all ${
                statistics.pendingListings > 0
                  ? "bg-amber-100 border-amber-400 shadow-md"
                  : "bg-amber-50 border-amber-200"
              } ${
                adminListings.length > 0
                  ? "cursor-pointer hover:shadow-lg hover:scale-105"
                  : "cursor-default"
              }`}
            >
              {statistics.pendingListings > 0 && (
                <div className="text-amber-600 text-xl mb-1">⚠️</div>
              )}
              <div
                className={`font-bold ${
                  statistics.pendingListings > 0
                    ? "text-4xl text-amber-700"
                    : "text-2xl text-amber-600"
                }`}
              >
                {statistics.pendingListings}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Approved: {statistics.approvedListings} | Total:{" "}
                {statistics.totalListings}
              </div>
              <div className="text-sm text-gray-600 mt-1">Listings Pending</div>
            </div>
          </div>
        </div>
      </div>

      {/* Channel Partners pending approval */}
      {channelPartners.length > 0 && (
        <div ref={channelPartnersRef} className="mb-8">
          <h3 className="text-2xl font-bold mb-4 text-gray-800 text-center">
            Channel Partners Pending Approval
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] text-center bg-white shadow rounded-lg">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 border text-sm sm:text-base">
                    Company Name
                  </th>
                  <th className="p-3 border text-sm sm:text-base">Contact</th>
                  <th className="p-3 border text-sm sm:text-base">
                    Investment Capacity
                  </th>
                  <th className="p-3 border text-sm sm:text-base">
                    Experience
                  </th>
                  <th className="p-3 border text-sm sm:text-base">Status</th>
                  <th className="p-3 border text-sm sm:text-base">Actions</th>
                </tr>
              </thead>
              <tbody>
                {channelPartners.map((cp) => (
                  <tr key={cp.id} className="hover:bg-gray-50">
                    <td className="p-3 border">
                      {cp.channelPartnerProfile?.companyName ||
                        cp.channelPartnerProfile?.name}
                    </td>
                    <td className="p-3 border">
                      <div className="text-sm">{cp.email}</div>
                      <div className="text-sm text-gray-500">
                        {cp.phoneNumber}
                      </div>
                    </td>
                    <td className="p-3 border">
                      {cp.channelPartnerProfile?.investmentCapacity || "-"}
                    </td>
                    <td className="p-3 border">
                      {cp.channelPartnerProfile?.experience || "-"} years
                    </td>
                    <td className="p-3 border capitalize">
                      {cp.channelPartnerProfile?.status || cp.status}
                    </td>
                    <td className="p-3 border space-x-2">
                      <button
                        className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition"
                        onClick={async () => {
                          try {
                            const companyName =
                              cp.channelPartnerProfile?.companyName ||
                              cp.channelPartnerProfile?.name;
                            const confirmed = window.confirm(
                              `Approve Channel Partner "${companyName}"?`
                            );
                            if (!confirmed) return;
                            await adminApi.approveChannelPartner(
                              cp.channelPartnerProfile.id
                            );
                            // Refresh the list and statistics
                            const refreshed =
                              await adminApi.listChannelPartners({
                                status: "pending",
                                page: 1,
                                limit: 20,
                              });
                            setChannelPartners(refreshed?.items || []);
                            await refreshStatistics();
                          } catch (_e) {
                            alert("Failed to approve");
                          }
                        }}
                      >
                        Approve
                      </button>
                      <button
                        className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition"
                        onClick={async () => {
                          const companyName =
                            cp.channelPartnerProfile?.companyName ||
                            cp.channelPartnerProfile?.name;
                          const confirmed = window.confirm(
                            `Reject Channel Partner "${companyName}"?`
                          );
                          if (!confirmed) return;
                          const reason =
                            window.prompt("Rejection reason (optional)") ||
                            undefined;
                          try {
                            await adminApi.rejectChannelPartner(
                              cp.channelPartnerProfile.id,
                              reason
                            );
                            // Refresh the list and statistics
                            const refreshed =
                              await adminApi.listChannelPartners({
                                status: "pending",
                                page: 1,
                                limit: 20,
                              });
                            setChannelPartners(refreshed?.items || []);
                            await refreshStatistics();
                          } catch (_e) {
                            alert("Failed to reject");
                          }
                        }}
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Promoters pending approval */}
      {promoters.length > 0 && (
        <div ref={promotersRef} className="mb-8">
          <h3 className="text-2xl font-bold mb-4 text-gray-800 text-center">
            Promoters Pending Approval
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-center bg-white shadow rounded-lg">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 border text-sm sm:text-base">
                    Name & Details
                  </th>
                  <th className="p-3 border text-sm sm:text-base">Contact</th>
                  <th className="p-3 border text-sm sm:text-base">
                    Channel Partner
                  </th>
                  <th className="p-3 border text-sm sm:text-base">
                    Payment Status
                  </th>
                  <th className="p-3 border text-sm sm:text-base">Documents</th>
                  <th className="p-3 border text-sm sm:text-base">Status</th>
                  <th className="p-3 border text-sm sm:text-base">Actions</th>
                </tr>
              </thead>
              <tbody>
                {promoters.map((promoter) => (
                  <tr key={promoter.id} className="hover:bg-gray-50">
                    <td className="p-3 border text-left">
                      <div className="space-y-1">
                        <div className="font-medium">
                          {promoter.promoterProfile?.name || promoter.email}
                        </div>
                        {promoter.promoterProfile?.fatherName && (
                          <div className="text-sm text-gray-600">
                            Father: {promoter.promoterProfile.fatherName}
                          </div>
                        )}
                        {promoter.promoterProfile?.bloodGroup && (
                          <div className="text-sm text-gray-600">
                            Blood Group: {promoter.promoterProfile.bloodGroup}
                          </div>
                        )}
                        {promoter.promoterProfile?.qualification && (
                          <div className="text-sm text-gray-600">
                            Qualification:{" "}
                            {promoter.promoterProfile.qualification}
                          </div>
                        )}
                        {promoter.promoterProfile?.panNumber && (
                          <div className="text-sm text-gray-600">
                            PAN: {promoter.promoterProfile.panNumber}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-3 border">
                      <div className="text-sm">{promoter.email}</div>
                      <div className="text-sm text-gray-500">
                        {promoter.phoneNumber}
                      </div>
                      {promoter.promoterProfile?.address && (
                        <div className="text-xs text-gray-500 mt-1">
                          {promoter.promoterProfile.address.city},{" "}
                          {promoter.promoterProfile.address.state}
                        </div>
                      )}
                    </td>
                    <td className="p-3 border">
                      <div className="text-sm">
                        {promoter.channelPartnerId?.email || "Unknown"}
                      </div>
                      {promoter.channelPartnerId && (
                        <div className="text-xs text-gray-500">
                          ID: {promoter.channelPartnerId?.id || "N/A"}
                        </div>
                      )}
                    </td>
                    <td className="p-3 border capitalize">
                      <div className="space-y-1">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            promoter.paymentStatus === "verified"
                              ? "bg-green-100 text-green-800"
                              : promoter.paymentStatus === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {promoter.paymentStatus || "unpaid"}
                        </span>
                        {promoter.promoterProfile?.paymentDetails && (
                          <div className="text-xs text-gray-600">
                            ₹{promoter.promoterProfile.paymentDetails.amount}
                          </div>
                        )}
                        {promoter.promoterProfile?.paymentDetails?.utrId && (
                          <div className="text-xs text-gray-500">
                            UTR: {promoter.promoterProfile.paymentDetails.utrId}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-3 border">
                      <DocumentList
                        documents={promoter.promoterProfile?.documents}
                        maxDisplay={2}
                      />
                    </td>
                    <td className="p-3 border capitalize">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          promoter.promoterProfile?.status === "approved"
                            ? "bg-green-100 text-green-800"
                            : promoter.promoterProfile?.status ===
                              "pending_approval"
                            ? "bg-yellow-100 text-yellow-800"
                            : promoter.promoterProfile?.status ===
                              "pending_verification"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {promoter.promoterProfile?.status || promoter.status}
                      </span>
                    </td>
                    <td className="p-3 border space-x-1">
                      <button
                        className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                        onClick={() => setSelectedPromoter(promoter)}
                      >
                        View
                      </button>

                      {/* Payment Verification Buttons */}
                      {promoter.promoterProfile?.status ===
                        "pending_approval" &&
                        promoter.promoterProfile?.paymentDetails
                          ?.paymentStatus === "pending" && (
                          <>
                            <button
                              className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition"
                              onClick={async () => {
                                try {
                                  const promoterName =
                                    promoter.promoterProfile?.name ||
                                    promoter.email;
                                  const confirmed = window.confirm(
                                    `Verify payment for Promoter "${promoterName}"?`
                                  );
                                  if (!confirmed) return;

                                  await adminApi.verifyPayment(
                                    promoter.id, // Use user ID, not promoter profile ID
                                    true,
                                    "Payment verified by admin"
                                  );

                                  // Refresh the list and statistics
                                  const refreshed =
                                    await adminApi.listPromoters({
                                      status: "pending_approval",
                                      page: 1,
                                      limit: 20,
                                    });
                                  setPromoters(refreshed?.items || []);
                                  await refreshStatistics();
                                } catch (_e) {
                                  console.error(
                                    "Payment verification error:",
                                    _e
                                  );
                                  alert(
                                    `Failed to verify payment: ${
                                      _e?.message ||
                                      _e?.data?.message ||
                                      "Unknown error"
                                    }`
                                  );
                                }
                              }}
                            >
                              Verify Payment
                            </button>
                            <button
                              className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition"
                              onClick={async () => {
                                const promoterName =
                                  promoter.promoterProfile?.name ||
                                  promoter.email;
                                const confirmed = window.confirm(
                                  `Reject payment for Promoter "${promoterName}"?`
                                );
                                if (!confirmed) return;
                                const reason =
                                  window.prompt(
                                    "Rejection reason (optional)"
                                  ) || undefined;
                                try {
                                  await adminApi.verifyPayment(
                                    promoter.id, // Use user ID, not promoter profile ID
                                    false,
                                    reason
                                  );
                                  // Refresh the list and statistics
                                  const refreshed =
                                    await adminApi.listPromoters({
                                      status: "pending_approval",
                                      page: 1,
                                      limit: 20,
                                    });
                                  setPromoters(refreshed?.items || []);
                                  await refreshStatistics();
                                } catch (_e) {
                                  alert("Failed to reject payment");
                                }
                              }}
                            >
                              Reject Payment
                            </button>
                          </>
                        )}

                      <button
                        className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition"
                        onClick={async () => {
                          try {
                            const promoterName =
                              promoter.promoterProfile?.name || promoter.email;
                            const confirmed = window.confirm(
                              `Approve Promoter "${promoterName}"?`
                            );
                            if (!confirmed) return;
                            await adminApi.approvePromoter(
                              promoter.promoterProfile.id
                            );
                            // Refresh the list and statistics
                            const refreshed = await adminApi.listPromoters({
                              status: "pending_approval",
                              page: 1,
                              limit: 20,
                            });
                            setPromoters(refreshed?.items || []);
                            await refreshStatistics();
                          } catch (_e) {
                            alert("Failed to approve");
                          }
                        }}
                      >
                        Approve
                      </button>
                      <button
                        className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition"
                        onClick={async () => {
                          const promoterName =
                            promoter.promoterProfile?.name || promoter.email;
                          const confirmed = window.confirm(
                            `Reject Promoter "${promoterName}"?`
                          );
                          if (!confirmed) return;
                          const reason =
                            window.prompt("Rejection reason (optional)") ||
                            undefined;
                          try {
                            await adminApi.rejectPromoter(
                              promoter.promoterProfile.id,
                              reason
                            );
                            // Refresh the list and statistics
                            const refreshed = await adminApi.listPromoters({
                              status: "pending_approval",
                              page: 1,
                              limit: 20,
                            });
                            setPromoters(refreshed?.items || []);
                            await refreshStatistics();
                          } catch (_e) {
                            alert("Failed to reject");
                          }
                        }}
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Listings pending approval */}
      {adminListings.length > 0 && (
        <div ref={listingsRef} className="mb-8">
          <h3 className="text-2xl font-bold mb-4 text-gray-800 text-center">
            Listings Pending Approval
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px] text-center bg-white shadow rounded-lg">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 border text-sm sm:text-base">
                    Name & Description
                  </th>
                  <th className="p-3 border text-sm sm:text-base">Price</th>
                  <th className="p-3 border text-sm sm:text-base">Category</th>
                  <th className="p-3 border text-sm sm:text-base">Location</th>
                  <th className="p-3 border text-sm sm:text-base">Provider</th>
                  <th className="p-3 border text-sm sm:text-base">Status</th>
                  <th className="p-3 border text-sm sm:text-base">Actions</th>
                </tr>
              </thead>
              <tbody>
                {adminListings.map((l) => (
                  <tr key={l._id} className="hover:bg-gray-50">
                    <td className="p-3 border text-left">
                      <div className="space-y-1">
                        <div className="font-medium">{l.name}</div>
                        <div className="text-sm text-gray-600 max-w-xs truncate">
                          {l.description}
                        </div>
                        {l.tags && l.tags.length > 0 && (
                          <div className="text-xs text-blue-600">
                            Tags: {l.tags.slice(0, 2).join(", ")}
                            {l.tags.length > 2 && ` +${l.tags.length - 2} more`}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-3 border">
                      {typeof l.price === "number"
                        ? currencyFmt.format(l.price)
                        : "-"}
                    </td>
                    <td className="p-3 border">
                      <div className="text-sm">
                        {l.categoryId?.name || "Unknown"}
                      </div>
                      {l.subcategoryId?.name && (
                        <div className="text-xs text-gray-500">
                          {l.subcategoryId.name}
                        </div>
                      )}
                    </td>
                    <td className="p-3 border">
                      <div className="text-sm">{l.address}</div>
                      {l.maxRadiusKm && (
                        <div className="text-xs text-gray-500">
                          Radius: {l.maxRadiusKm}km
                        </div>
                      )}
                    </td>
                    <td className="p-3 border">
                      <div className="text-sm">
                        {providerIdToName[l.providerUserId] ||
                          (typeof l.providerUserId === "object"
                            ? l.providerUserId?.email || l.providerUserId?._id
                            : l.providerUserId) ||
                          "Unknown"}
                      </div>
                      {l.phoneNumber && (
                        <div className="text-xs text-gray-500">
                          {l.phoneNumber}
                        </div>
                      )}
                    </td>
                    <td className="p-3 border capitalize">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          l.status === "approved"
                            ? "bg-green-100 text-green-800"
                            : l.status === "pending_approval"
                            ? "bg-yellow-100 text-yellow-800"
                            : l.status === "rejected"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {l.status}
                      </span>
                    </td>
                    <td className="p-3 border space-x-1 space-y-3">
                      <button
                        className="px-6 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                        onClick={() => setSelectedListing(l)}
                      >
                        View
                      </button>

                      <button
                        className="px-6 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition"
                        onClick={async () => {
                          try {
                            const providerName =
                              providerIdToName[l.providerUserId] ||
                              (typeof l.providerUserId === "object"
                                ? l.providerUserId?.email ||
                                  l.providerUserId?._id
                                : l.providerUserId) ||
                              "Unknown";
                            const confirmed = window.confirm(
                              `Approve listing "${l.name}" by ${providerName}?`
                            );
                            if (!confirmed) return;
                            await adminApi.approveListing(l._id);
                            const refreshed = await adminApi.listListings({
                              status: "pending_approval",
                              page: 1,
                              limit: 20,
                            });
                            setAdminListings(refreshed?.items || []);
                            await refreshStatistics();
                          } catch (_e) {
                            alert("Failed to approve");
                          }
                        }}
                      >
                        Approve
                      </button>
                      <button
                        className="px-6 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition"
                        onClick={async () => {
                          const providerName =
                            providerIdToName[l.providerUserId] ||
                            (typeof l.providerUserId === "object"
                              ? l.providerUserId?.email || l.providerUserId?._id
                              : l.providerUserId) ||
                            "Unknown";
                          const confirmed = window.confirm(
                            `Reject listing "${l.name}" by ${providerName}?`
                          );
                          if (!confirmed) return;
                          const reason =
                            window.prompt("Rejection reason (optional)") ||
                            undefined;
                          try {
                            await adminApi.rejectListing(l._id, reason);
                            const refreshed = await adminApi.listListings({
                              status: "pending_approval",
                              page: 1,
                              limit: 20,
                            });
                            setAdminListings(refreshed?.items || []);
                            await refreshStatistics();
                          } catch (_e) {
                            alert("Failed to reject");
                          }
                        }}
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Provider Payment Verification Section */}
      {providerPaymentVerification.length > 0 && (
        <div ref={providerPaymentsRef} className="mb-8">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 mb-6">
            <h3 className="text-2xl font-bold text-white text-center mb-2">
              Provider Payment Verification
            </h3>
            <p className="text-indigo-100 text-center">
              {providerPaymentVerification.length} provider
              {providerPaymentVerification.length !== 1 ? "s" : ""} waiting for
              payment verification
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px] text-center bg-white shadow rounded-lg">
              <thead className="bg-indigo-50">
                <tr>
                  <th className="p-4 border text-sm sm:text-base font-semibold text-gray-700">
                    Provider Details
                  </th>
                  <th className="p-4 border text-sm sm:text-base font-semibold text-gray-700">
                    Package Info
                  </th>
                  <th className="p-4 border text-sm sm:text-base font-semibold text-gray-700">
                    Payment Details
                  </th>
                  <th className="p-4 border text-sm sm:text-base font-semibold text-gray-700">
                    UTR/Transaction
                  </th>
                  <th className="p-4 border text-sm sm:text-base font-semibold text-gray-700">
                    Payment Date
                  </th>
                  <th className="p-4 border text-sm sm:text-base font-semibold text-gray-700">
                    Status
                  </th>
                  <th className="p-4 border text-sm sm:text-base font-semibold text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {providerPaymentVerification.map((provider) => (
                  <tr
                    key={provider.id}
                    className="hover:bg-indigo-50 transition-colors"
                  >
                    <td className="p-4 border text-left">
                      <div className="space-y-2">
                        <div className="font-semibold text-gray-900">
                          {provider?.providerProfile?.companyName ||
                            provider?.email}
                        </div>
                        <div className="text-sm text-gray-600">
                          {provider?.email}
                        </div>
                        <div className="text-xs text-gray-500">
                          📞 {provider?.providerProfile?.phoneNumber || "N/A"}
                        </div>
                        {provider?.providerProfile?.companyType && (
                          <div className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full inline-block">
                            {provider.providerProfile.companyType
                              .replace("_", " ")
                              .toUpperCase()}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-4 border">
                      <div className="space-y-1">
                        <div className="text-lg font-bold text-indigo-600">
                          {provider?.providerProfile?.numberOfListings || 0}
                        </div>
                        <div className="text-sm text-gray-600">
                          Listings Package
                        </div>
                        {provider?.providerProfile?.verifiedNumberOfListings >
                          0 && (
                          <div className="text-xs text-blue-600">
                            Upgrading from{" "}
                            {provider.providerProfile.verifiedNumberOfListings}
                          </div>
                        )}
                        <div className="text-xs text-gray-500">Requested</div>
                      </div>
                    </td>
                    <td className="p-4 border">
                      <div className="space-y-1">
                        <div className="text-lg font-bold text-green-600">
                          {provider?.providerProfile?.packageAmount
                            ? currencyFmt.format(
                                provider.providerProfile.packageAmount
                              )
                            : "Not specified"}
                        </div>
                        <div className="text-xs text-gray-500">
                          Package Amount
                        </div>
                      </div>
                    </td>
                    <td className="p-4 border">
                      <div className="space-y-1">
                        <div className="text-sm font-mono bg-gray-100 p-2 rounded">
                          {provider?.providerProfile?.utrId ||
                            provider?.utrId ||
                            "Not provided"}
                        </div>
                        <div className="text-xs text-gray-500">
                          Transaction ID
                        </div>
                      </div>
                    </td>
                    <td className="p-4 border">
                      <div className="space-y-1">
                        <div className="text-sm font-medium">
                          {provider?.createdAt
                            ? new Date(provider.createdAt).toLocaleDateString()
                            : "-"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {provider?.createdAt
                            ? new Date(provider.createdAt).toLocaleTimeString()
                            : ""}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 border">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
                        Pending Verification
                      </span>
                    </td>
                    <td className=" justify-center items-center p-4 border space-x-2 ">
                      <button
                        className="px-4 py-4 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md whitespace-nowrap"
                        onClick={async () => {
                          const companyName =
                            provider?.providerProfile?.companyName ||
                            provider?.email;
                          const confirmed = window.confirm(
                            `Verify payment for "${companyName}"?\n\nAmount: ${
                              provider?.providerProfile?.packageAmount
                                ? currencyFmt.format(
                                    provider.providerProfile.packageAmount
                                  )
                                : "Not specified"
                            }\nUTR: ${
                              provider?.providerProfile?.utrId ||
                              provider?.utrId ||
                              "Not provided"
                            }`
                          );
                          if (!confirmed) return;

                          try {
                            await adminApi.verifyProviderPayment(
                              provider.id,
                              true
                            );
                            alert("✅ Payment verified successfully!");
                            // Refresh the data
                            const refreshed = await adminApi.listProviders({
                              page: 1,
                              limit: 1000,
                            });
                            const pendingPaymentProviders = (
                              refreshed?.items || []
                            ).filter((p) => p?.paymentStatus === "pending");
                            setProviderPaymentVerification(
                              pendingPaymentProviders
                            );
                            setProviders(refreshed?.items || []);
                            await refreshStatistics();
                          } catch (error) {
                            console.error("Verify payment error:", error);
                            alert(
                              "❌ Failed to verify payment: " +
                                (error?.data?.message ||
                                  error?.message ||
                                  "Unknown error")
                            );
                          }
                        }}
                      >
                        ✅ Verify Payment
                      </button>
                      <button
                        className="px-4 py-4 mt-4 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md whitespace-nowrap"
                        onClick={async () => {
                          const companyName =
                            provider?.providerProfile?.companyName ||
                            provider?.email;
                          const confirmed = window.confirm(
                            `Reject payment for "${companyName}"?\n\nAmount: ${
                              provider?.providerProfile?.packageAmount
                                ? currencyFmt.format(
                                    provider.providerProfile.packageAmount
                                  )
                                : "Not specified"
                            }\nUTR: ${
                              provider?.providerProfile?.utrId ||
                              provider?.utrId ||
                              "Not provided"
                            }`
                          );
                          if (!confirmed) return;

                          window.prompt(
                            "Please provide a reason for rejection (optional):"
                          );

                          try {
                            await adminApi.verifyProviderPayment(
                              provider.id,
                              false
                            );
                            alert("❌ Payment rejected!");
                            // Refresh the data
                            const refreshed = await adminApi.listProviders({
                              page: 1,
                              limit: 1000,
                            });
                            const pendingPaymentProviders = (
                              refreshed?.items || []
                            ).filter((p) => p?.paymentStatus === "pending");
                            setProviderPaymentVerification(
                              pendingPaymentProviders
                            );
                            setProviders(refreshed?.items || []);
                            await refreshStatistics();
                          } catch (error) {
                            console.error("Reject payment error:", error);
                            alert(
                              "❌ Failed to reject payment: " +
                                (error?.data?.message ||
                                  error?.message ||
                                  "Unknown error")
                            );
                          }
                        }}
                      >
                        ❌ Reject Payment
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No pending requests message */}
      {channelPartners.length === 0 &&
        promoters.length === 0 &&
        adminListings.length === 0 &&
        providerPaymentVerification.length === 0 && (
          <div className="bg-white shadow-xl rounded-xl p-8 border border-gray-200 w-full max-w-3xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white text-3xl mx-auto mb-4">
              ✅
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              All caught up!
            </h3>
            <p className="text-gray-500">
              No pending approval requests or payment verifications at the
              moment.
            </p>
          </div>
        )}

      {/* Promoter Details Modal */}
      {selectedPromoter && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Promoter Details</h3>
              <button
                onClick={() => setSelectedPromoter(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Personal Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Personal Information</h4>
                <div className="space-y-2">
                  <p>
                    <strong>Name:</strong>{" "}
                    {selectedPromoter.promoterProfile?.name ||
                      selectedPromoter.email}
                  </p>
                  <p>
                    <strong>Father's Name:</strong>{" "}
                    {selectedPromoter.promoterProfile?.fatherName || "N/A"}
                  </p>
                  <p>
                    <strong>Email:</strong> {selectedPromoter.email}
                  </p>
                  <p>
                    <strong>Phone:</strong>{" "}
                    {selectedPromoter.phoneNumber || "N/A"}
                  </p>
                  <p>
                    <strong>Blood Group:</strong>{" "}
                    {selectedPromoter.promoterProfile?.bloodGroup || "N/A"}
                  </p>
                  <p>
                    <strong>Qualification:</strong>{" "}
                    {selectedPromoter.promoterProfile?.qualification || "N/A"}
                  </p>
                  <p>
                    <strong>PAN Number:</strong>{" "}
                    {selectedPromoter.promoterProfile?.panNumber || "N/A"}
                  </p>
                </div>
              </div>

              {/* Address Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Address</h4>
                {selectedPromoter.promoterProfile?.address ? (
                  <div className="space-y-2">
                    <p>
                      <strong>Street:</strong>{" "}
                      {selectedPromoter.promoterProfile.address.street}
                    </p>
                    <p>
                      <strong>City:</strong>{" "}
                      {selectedPromoter.promoterProfile.address.city}
                    </p>
                    <p>
                      <strong>State:</strong>{" "}
                      {selectedPromoter.promoterProfile.address.state}
                    </p>
                    <p>
                      <strong>Pincode:</strong>{" "}
                      {selectedPromoter.promoterProfile.address.pincode}
                    </p>
                    <p>
                      <strong>Country:</strong>{" "}
                      {selectedPromoter.promoterProfile.address.country}
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-500">No address information</p>
                )}
              </div>

              {/* Payment Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Payment Information</h4>
                {selectedPromoter.promoterProfile?.paymentDetails ? (
                  <div className="space-y-2">
                    <p>
                      <strong>Amount:</strong> ₹
                      {selectedPromoter.promoterProfile.paymentDetails.amount}
                    </p>
                    <p>
                      <strong>UTR ID:</strong>{" "}
                      {selectedPromoter.promoterProfile.paymentDetails.utrId ||
                        "Not provided"}
                    </p>
                    <p>
                      <strong>Status:</strong>
                      <span
                        className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                          selectedPromoter.promoterProfile.paymentDetails
                            .paymentStatus === "verified"
                            ? "bg-green-100 text-green-800"
                            : selectedPromoter.promoterProfile.paymentDetails
                                .paymentStatus === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {
                          selectedPromoter.promoterProfile.paymentDetails
                            .paymentStatus
                        }
                      </span>
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-500">No payment information</p>
                )}
              </div>

              {/* Status Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Status</h4>
                <div className="space-y-2">
                  <p>
                    <strong>Profile Status:</strong>
                    <span
                      className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                        selectedPromoter.promoterProfile?.status === "approved"
                          ? "bg-green-100 text-green-800"
                          : selectedPromoter.promoterProfile?.status ===
                            "pending_approval"
                          ? "bg-yellow-100 text-yellow-800"
                          : selectedPromoter.promoterProfile?.status ===
                            "pending_verification"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {selectedPromoter.promoterProfile?.status ||
                        selectedPromoter.status}
                    </span>
                  </p>
                  <p>
                    <strong>Channel Partner:</strong>{" "}
                    {selectedPromoter.channelPartnerId?.email || "Unknown"}
                  </p>
                  {selectedPromoter.channelPartnerId && (
                    <p>
                      <strong>Channel Partner ID:</strong>{" "}
                      {selectedPromoter.channelPartnerId._id || "N/A"}
                    </p>
                  )}
                  <p>
                    <strong>Commission Rate:</strong>{" "}
                    {selectedPromoter.promoterProfile?.commissionRate || 0}%
                  </p>
                  <p>
                    <strong>Providers Created:</strong>{" "}
                    {selectedPromoter.promoterProfile?.totalProvidersCreated ||
                      0}
                  </p>
                </div>
              </div>
            </div>

            {/* Documents Section */}
            <div className="mt-6">
              <h4 className="font-semibold text-lg mb-3 text-red-800">
                Documents (
                {(() => {
                  const docs = selectedPromoter.promoterProfile?.documents;
                  if (Array.isArray(docs)) {
                    return docs.length;
                  } else if (docs && typeof docs === "object") {
                    return Object.keys(docs).length;
                  }
                  return 0;
                })()}
                )
              </h4>
              <div className="mt-3 max-h-72 overflow-y-auto border rounded">
                {(() => {
                  const docs = selectedPromoter.promoterProfile?.documents;
                  const hasDocuments = Array.isArray(docs)
                    ? docs.length > 0
                    : docs &&
                      typeof docs === "object" &&
                      Object.keys(docs).length > 0;

                  if (!hasDocuments) {
                    return (
                      <div className="p-4 text-sm text-gray-500">
                        No documents uploaded
                      </div>
                    );
                  }

                  // Handle array format (full document objects)
                  if (Array.isArray(docs)) {
                    return (
                      <table className="w-full text-left text-sm min-w-[600px]">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-2 border">Preview</th>
                            <th className="p-2 border">Name</th>
                            <th className="p-2 border">Type</th>
                            <th className="p-2 border">Size</th>
                            <th className="p-2 border">Uploaded</th>
                            <th className="p-2 border">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {docs.map((d) => {
                            const isImage = (d?.mimeType || "").startsWith(
                              "image/"
                            );
                            return (
                              <tr key={d.id}>
                                <td className="p-2 border align-middle">
                                  {isImage ? (
                                    <button
                                      className="inline-flex items-center"
                                      onClick={() =>
                                        window.open(d.url, "_blank", "noopener")
                                      }
                                      title="Open image"
                                    >
                                      <img
                                        src={d.url}
                                        alt={
                                          d.originalName || d.kind || "document"
                                        }
                                        className="h-12 w-12 object-cover rounded"
                                      />
                                    </button>
                                  ) : (
                                    <div className="flex items-center text-gray-600">
                                      <span className="text-lg">📄</span>
                                      <span className="ml-1">File</span>
                                    </div>
                                  )}
                                </td>
                                <td className="p-2 border align-middle">
                                  {d.originalName || d.kind || "-"}
                                </td>
                                <td className="p-2 border align-middle">
                                  {d.mimeType || "-"}
                                </td>
                                <td className="p-2 border align-middle">
                                  {d.size
                                    ? `${(d.size / 1024).toFixed(1)} KB`
                                    : "-"}
                                </td>
                                <td className="p-2 border align-middle">
                                  {d.uploadedAt
                                    ? new Date(
                                        d.uploadedAt
                                      ).toLocaleDateString()
                                    : "-"}
                                </td>
                                <td className="p-2 border align-middle">
                                  <div className="flex items-center gap-2">
                                    <button
                                      className="px-2 py-1 text-blue-600 hover:text-blue-800 underline"
                                      onClick={() =>
                                        window.open(d.url, "_blank", "noopener")
                                      }
                                    >
                                      Open
                                    </button>
                                    <a
                                      className="px-2 py-1 text-gray-700 hover:text-gray-900 underline"
                                      href={d.url}
                                      download={d.originalName || true}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Download
                                    </a>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    );
                  }

                  // Handle object format (document IDs)
                  if (docs && typeof docs === "object") {
                    const documentTypes = {
                      experienceDocuments: "Experience Documents",
                      panCardImage: "PAN Card",
                      kycDocument: "KYC Document",
                      kycImage: "KYC Image",
                      cvUpload: "CV Upload",
                      photoUpload: "Photo Upload",
                    };

                    return (
                      <table className="w-full text-left text-sm min-w-[600px]">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-2 border">Document Type</th>
                            <th className="p-2 border">Document ID</th>
                            <th className="p-2 border">Status</th>
                            <th className="p-2 border">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(docs).map(([key, docId]) => (
                            <tr key={key}>
                              <td className="p-2 border align-middle">
                                <div className="flex items-center text-gray-600">
                                  <span className="text-lg mr-2">📄</span>
                                  <span className="font-medium">
                                    {documentTypes[key] || key}
                                  </span>
                                </div>
                              </td>
                              <td className="p-2 border align-middle text-sm">
                                {docId}
                              </td>
                              <td className="p-2 border align-middle">
                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                  Uploaded
                                </span>
                              </td>
                              <td className="p-2 border align-middle">
                                <div className="flex items-center gap-2">
                                  <button
                                    className="px-2 py-1 text-blue-600 hover:text-blue-800 underline"
                                    onClick={() =>
                                      window.open(
                                        `/uploads/${docId}`,
                                        "_blank",
                                        "noopener"
                                      )
                                    }
                                  >
                                    View
                                  </button>
                                  <a
                                    className="px-2 py-1 text-gray-700 hover:text-gray-900 underline"
                                    href={`/uploads/${docId}`}
                                    download
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Download
                                  </a>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  }

                  return (
                    <div className="p-4 text-sm text-gray-500">
                      No documents uploaded
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
              {/* Payment Verification Buttons */}
              {selectedPromoter.promoterProfile?.status ===
                "pending_approval" &&
                selectedPromoter.promoterProfile?.paymentDetails
                  ?.paymentStatus === "pending" && (
                  <>
                    <button
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
                      onClick={async () => {
                        try {
                          const promoterName =
                            selectedPromoter.promoterProfile?.name ||
                            selectedPromoter.email;
                          const confirmed = window.confirm(
                            `Verify payment for Promoter "${promoterName}"?`
                          );
                          if (!confirmed) return;
                          await adminApi.verifyPayment(
                            selectedPromoter.id, // Use user ID, not promoter profile ID
                            true,
                            "Payment verified by admin"
                          );
                          setSelectedPromoter(null);
                          // Refresh the list and statistics
                          const refreshed = await adminApi.listPromoters({
                            status: "pending_approval",
                            page: 1,
                            limit: 20,
                          });
                          setPromoters(refreshed?.items || []);
                          await refreshStatistics();
                        } catch (_e) {
                          alert("Failed to verify payment");
                        }
                      }}
                    >
                      Verify Payment
                    </button>
                    <button
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
                      onClick={async () => {
                        const promoterName =
                          selectedPromoter.promoterProfile?.name ||
                          selectedPromoter.email;
                        const confirmed = window.confirm(
                          `Reject payment for Promoter "${promoterName}"?`
                        );
                        if (!confirmed) return;
                        const reason =
                          window.prompt("Rejection reason (optional)") ||
                          undefined;
                        try {
                          await adminApi.verifyPayment(
                            selectedPromoter.id, // Use user ID, not promoter profile ID
                            false,
                            reason
                          );
                          setSelectedPromoter(null);
                          // Refresh the list and statistics
                          const refreshed = await adminApi.listPromoters({
                            status: "pending_approval",
                            page: 1,
                            limit: 20,
                          });
                          setPromoters(refreshed?.items || []);
                          await refreshStatistics();
                        } catch (_e) {
                          alert("Failed to reject payment");
                        }
                      }}
                    >
                      Reject Payment
                    </button>
                  </>
                )}

              <button
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
                onClick={async () => {
                  try {
                    const promoterName =
                      selectedPromoter.promoterProfile?.name ||
                      selectedPromoter.email;
                    const confirmed = window.confirm(
                      `Approve Promoter "${promoterName}"?`
                    );
                    if (!confirmed) return;
                    await adminApi.approvePromoter(
                      selectedPromoter.promoterProfile.id
                    );
                    setSelectedPromoter(null);
                    // Refresh the list and statistics
                    const refreshed = await adminApi.listPromoters({
                      status: "pending_approval",
                      page: 1,
                      limit: 20,
                    });
                    setPromoters(refreshed?.items || []);
                    await refreshStatistics();
                  } catch (_e) {
                    alert("Failed to approve");
                  }
                }}
              >
                Approve Promoter
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
                onClick={async () => {
                  const promoterName =
                    selectedPromoter.promoterProfile?.name ||
                    selectedPromoter.email;
                  const confirmed = window.confirm(
                    `Reject Promoter "${promoterName}"?`
                  );
                  if (!confirmed) return;
                  const reason =
                    window.prompt("Rejection reason (optional)") || undefined;
                  try {
                    await adminApi.rejectPromoter(
                      selectedPromoter.promoterProfile.id,
                      reason
                    );
                    setSelectedPromoter(null);
                    // Refresh the list and statistics
                    const refreshed = await adminApi.listPromoters({
                      status: "pending_approval",
                      page: 1,
                      limit: 20,
                    });
                    setPromoters(refreshed?.items || []);
                    await refreshStatistics();
                  } catch (_e) {
                    alert("Failed to reject");
                  }
                }}
              >
                Reject Promoter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Listing Details Modal */}
      {selectedListing && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Listing Details</h3>
              <button
                onClick={() => setSelectedListing(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Basic Information</h4>
                <div className="space-y-2">
                  <p>
                    <strong>Name:</strong> {selectedListing.name}
                  </p>
                  <p>
                    <strong>Description:</strong> {selectedListing.description}
                  </p>
                  <p>
                    <strong>Price:</strong>{" "}
                    {typeof selectedListing.price === "number"
                      ? currencyFmt.format(selectedListing.price)
                      : "Not specified"}
                  </p>
                  <p>
                    <strong>Phone Number:</strong> {selectedListing.phoneNumber}
                  </p>
                  <p>
                    <strong>Address:</strong> {selectedListing.address}
                  </p>
                  {selectedListing.maxRadiusKm && (
                    <p>
                      <strong>Service Radius:</strong>{" "}
                      {selectedListing.maxRadiusKm} km
                    </p>
                  )}
                </div>
              </div>

              {/* Category Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Category Information</h4>
                <div className="space-y-2">
                  <p>
                    <strong>Category:</strong>{" "}
                    {selectedListing.categoryId?.name || "Unknown"}
                  </p>
                  <p>
                    <strong>Subcategory:</strong>{" "}
                    {selectedListing.subcategoryId?.name || "Not specified"}
                  </p>
                  {selectedListing.tags && selectedListing.tags.length > 0 && (
                    <div>
                      <strong>Tags:</strong>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedListing.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Location Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Location Information</h4>
                <div className="space-y-2">
                  <p>
                    <strong>Address:</strong> {selectedListing.address}
                  </p>
                  {selectedListing.location && (
                    <>
                      <p>
                        <strong>Latitude:</strong>{" "}
                        {selectedListing.location.coordinates[1]}
                      </p>
                      <p>
                        <strong>Longitude:</strong>{" "}
                        {selectedListing.location.coordinates[0]}
                      </p>
                    </>
                  )}
                  {selectedListing.maxRadiusKm && (
                    <p>
                      <strong>Service Radius:</strong>{" "}
                      {selectedListing.maxRadiusKm} km
                    </p>
                  )}
                </div>
              </div>

              {/* Provider Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Provider Information</h4>
                <div className="space-y-2">
                  <p>
                    <strong>Provider:</strong>{" "}
                    {providerIdToName[selectedListing.providerUserId] ||
                      (typeof selectedListing.providerUserId === "object"
                        ? selectedListing.providerUserId?.email ||
                          selectedListing.providerUserId?._id
                        : selectedListing.providerUserId) ||
                      "Unknown"}
                  </p>
                  <p>
                    <strong>Provider ID:</strong>{" "}
                    {typeof selectedListing.providerUserId === "object"
                      ? selectedListing.providerUserId?._id
                      : selectedListing.providerUserId}
                  </p>
                  <p>
                    <strong>Contact Phone:</strong>{" "}
                    {selectedListing.phoneNumber}
                  </p>
                </div>
              </div>

              {/* Status Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Status Information</h4>
                <div className="space-y-2">
                  <p>
                    <strong>Listing Status:</strong>
                    <span
                      className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                        selectedListing.status === "approved"
                          ? "bg-green-100 text-green-800"
                          : selectedListing.status === "pending_approval"
                          ? "bg-yellow-100 text-yellow-800"
                          : selectedListing.status === "rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {selectedListing.status}
                    </span>
                  </p>
                  {selectedListing.approvedAt && (
                    <p>
                      <strong>Approved At:</strong>{" "}
                      {new Date(selectedListing.approvedAt).toLocaleString()}
                    </p>
                  )}
                  {selectedListing.rejectedAt && (
                    <p>
                      <strong>Rejected At:</strong>{" "}
                      {new Date(selectedListing.rejectedAt).toLocaleString()}
                    </p>
                  )}
                  {selectedListing.rejectionReason && (
                    <p>
                      <strong>Rejection Reason:</strong>{" "}
                      {selectedListing.rejectionReason}
                    </p>
                  )}
                  <p>
                    <strong>Created At:</strong>{" "}
                    {new Date(selectedListing.createdAt).toLocaleString()}
                  </p>
                  <p>
                    <strong>Updated At:</strong>{" "}
                    {new Date(selectedListing.updatedAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Images Section */}
            {selectedListing.images && selectedListing.images.length > 0 && (
              <div className="mt-6">
                <h4 className="font-semibold text-lg mb-3 text-red-800">
                  Images ({selectedListing.images.length})
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {selectedListing.images.map((image, index) => (
                    <div
                      key={index}
                      className="border rounded-lg overflow-hidden"
                    >
                      <img
                        src={image.url}
                        alt={image.alt || `Listing image ${index + 1}`}
                        className="w-full h-32 object-cover cursor-pointer hover:opacity-80 transition"
                        onClick={() =>
                          window.open(image.url, "_blank", "noopener")
                        }
                      />
                      <div className="p-2 text-xs text-gray-600">
                        <div className="truncate">
                          {image.alt || `Image ${index + 1}`}
                        </div>
                        <div>
                          {image.size
                            ? `${(image.size / 1024).toFixed(1)} KB`
                            : ""}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
              <button
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
                onClick={async () => {
                  try {
                    const providerName =
                      providerIdToName[selectedListing.providerUserId] ||
                      (typeof selectedListing.providerUserId === "object"
                        ? selectedListing.providerUserId?.email ||
                          selectedListing.providerUserId?._id
                        : selectedListing.providerUserId) ||
                      "Unknown";
                    const confirmed = window.confirm(
                      `Approve listing "${selectedListing.name}" by ${providerName}?`
                    );
                    if (!confirmed) return;
                    await adminApi.approveListing(selectedListing._id);
                    setSelectedListing(null);
                    // Refresh the list and statistics
                    const refreshed = await adminApi.listListings({
                      status: "pending_approval",
                      page: 1,
                      limit: 20,
                    });
                    setAdminListings(refreshed?.items || []);
                    await refreshStatistics();
                  } catch (_e) {
                    alert("Failed to approve");
                  }
                }}
              >
                Approve Listing
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
                onClick={async () => {
                  const providerName =
                    providerIdToName[selectedListing.providerUserId] ||
                    (typeof selectedListing.providerUserId === "object"
                      ? selectedListing.providerUserId?.email ||
                        selectedListing.providerUserId?._id
                      : selectedListing.providerUserId) ||
                    "Unknown";
                  const confirmed = window.confirm(
                    `Reject listing "${selectedListing.name}" by ${providerName}?`
                  );
                  if (!confirmed) return;
                  const reason =
                    window.prompt("Rejection reason (optional)") || undefined;
                  try {
                    await adminApi.rejectListing(selectedListing._id, reason);
                    setSelectedListing(null);
                    // Refresh the list and statistics
                    const refreshed = await adminApi.listListings({
                      status: "pending_approval",
                      page: 1,
                      limit: 20,
                    });
                    setAdminListings(refreshed?.items || []);
                    await refreshStatistics();
                  } catch (_e) {
                    alert("Failed to reject");
                  }
                }}
              >
                Reject Listing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
